/**
 * @fileoverview Dexie tenant database definition for tenant-web local-first data.
 * @module local-db/tenant-db
 *
 * Responsibilities:
 * - Define tenant Dexie schema versions and exposed collection wrappers.
 * - Provide singleton access to the `firesquirrel-tenant` IndexedDB database.
 *
 * Dependencies:
 * - dexie
 * - ../security/secureCollection
 *
 * Architecture:
 * - Tenant-web uses Dexie as the local-first source of truth for tenant and system-derived projections.
 * - Plain Dexie tables are used because tenant inspection data is public-record data and must remain indexable.
 *
 * Notes:
 * - System-owned user and tenant projections are stored locally via `/system-sync` tables.
 */

import Dexie from 'dexie';
import type { TableLike } from '../security/secureCollection';
import { documentConfigToLayout } from '@firesquirrel/document-render';
import type { MigrationMeta } from '../types/dbMeta';
import {
  TENANT_EXPECTED_DB_SCHEMA_VERSION,
  TENANT_MIN_MIGRATABLE_VERSION,
} from '@firesquirrel/sync-config';
import {
  canonicalizeContactDoc as sharedCanonicalizeContactDoc,
  deriveMailingAddressFromLegacy as sharedDeriveMailingAddressFromLegacy,
} from '../utils/contactCanonicalization';

// ---------------------------------------------------------------------------
// Schema version constants — re-exported from @firesquirrel/sync-config
// ---------------------------------------------------------------------------

/**
 * The current Dexie schema version the code expects. The localDatabaseManager
 * compares this against the `_db_meta.schemaVersion` in the local DB and the
 * server's `local_db_metadata.schemaVersion` to determine recovery action.
 *
 * When a developer adds a new `db.version(N)` they MUST update the constant in
 * `api/packages/sync-config/src/local-db-versions.ts` to match the new version
 * number. The local-db-schema-update skill enforces this.
 *
 * The canonical constant lives in `@firesquirrel/sync-config` so both the
 * frontend (localDatabaseManager) and the API (localDbMetadataService) share
 * a single source of truth — no drift between what the code compiled and what
 * the API seeded in MongoDB.
 */
export const EXPECTED_DB_SCHEMA_VERSION = TENANT_EXPECTED_DB_SCHEMA_VERSION;

/**
 * The oldest schema version for which migration scripts exist. If the local DB
 * is older than this, the manager wipes and rebuilds instead of attempting
 * incremental migration.
 *
 * v15 added `_sync_meta`; migrations from v15 onward are supported. Versions
 * before v15 used encryption and are not migratable.
 *
 * Canonical source: `@firesquirrel/sync-config` (`TENANT_MIN_MIGRATABLE_VERSION`).
 */
export const MIN_MIGRATABLE_VERSION = TENANT_MIN_MIGRATABLE_VERSION;

/**
 * Per-version migration metadata. The localDatabaseManager reads this to
 * determine whether writes should be held during a migration range.
 *
 * Phase 8 (deferred): entries with `holdWrites: true` will block writes until
 * the migration AND full re-sync complete. For Phase 1, all migrations default
 * to `holdWrites: false`.
 */
export const MIGRATION_REGISTRY: Record<number, MigrationMeta> = {
  40: { holdWrites: false, reason: 'Add _db_meta table for local database identity' },
  41: { holdWrites: false, reason: 'Drop obsolete tenant-owned import tables after import centralization' },
  43: { holdWrites: false, reason: 'Drop retired geo-import local caches' },
  44: { holdWrites: false, reason: 'Add complex_entity_groups pull-only table, entity complex-facet indexes, and complex_mutations local outbox (Complex Inspections Phase 1B)' },
  45: { holdWrites: false, reason: 'Add complex visit projection indexes to cases (Complex Inspections Phase 1C)' },
  46: { holdWrites: false, reason: 'Add compound group/member and grouped report/delivery indexes (Complex Inspections Phase 2A)' },
  47: { holdWrites: false, reason: 'Add inspection program and scheduling tables (#737 Phase 1)' },
  48: { holdWrites: false, reason: 'Add duplicate-detection indexes to cases (#737 Phase 5a)' },
  49: { holdWrites: false, reason: 'Add schedule_rule_catalog pull-only table (#737 Phase 6)' },
  50: { holdWrites: false, reason: 'Restore cases isArchived and complex visit indexes dropped by v48' },
  51: { holdWrites: false, reason: 'Add portal_requests and portal_change_requests pull-only tables (Customer Portal Phase 2)' },
  52: { holdWrites: false, reason: 'Add portal_activity_log pull-only table (Customer Portal Phase 2b)' },
  53: { holdWrites: false, reason: 'Add self_certification_submissions pull-only table (Customer Portal Phase 3)' },
  54: { holdWrites: false, reason: 'Add public_safety_surveys pull-only table (Customer Portal Phase 6)' },
  55: { holdWrites: false, reason: 'Add report parameter multi-entry indexes to report_definitions, system_report_definitions, and report_schedules' },
  56: { holdWrites: false, reason: 'Add entities.version index so the entity-grid change signature detects version/_deleted bumps without a new updatedAt' },
  57: { holdWrites: false, reason: 'Reconcile v32 deliveryStatus backfill with resolved distributionStatus on generated_documents' },
};

// ---------------------------------------------------------------------------
// Stamp updatedAt on every document written to Dexie (if not already set).
// This ensures the push checkpoint can always detect locally-written docs.
// ---------------------------------------------------------------------------
function stampDoc<T extends Record<string, any>>(doc: T): T {
  if (!doc.updatedAt) {
    return { ...doc, updatedAt: new Date().toISOString() };
  }
  return doc;
}

/**
 * IndexedDB omits a compound index entry when any key part is missing.
 * Receipt coverage indexes `deletedFlag` ('0' live, '1' tombstone). A numeric
 * 0 cannot lead a compound key. Server docs omit the field, so every inbound
 * write must stamp it.
 */
export function stampReceiptCoverageFlag<T>(doc: T): T {
  if (!doc || typeof doc !== 'object') return doc;
  const record = doc as Record<string, unknown>;
  const deletedFlag = record._deleted === true ? '1' : '0';
  if (record.deletedFlag === deletedFlag) return doc;
  return { ...record, deletedFlag } as T;
}

// ---------------------------------------------------------------------------
// DexieCollection — clean native Dexie wrapper (no RxDB shim)
//
// API:
//   toArray()              → all docs as plain objects
//   get(id)               → single doc or null
//   bulkGet(ids)          → many docs by id (order-preserving, misses → null)
//   put(doc)              → upsert, returns stamped doc
//   patch(id, patch)      → merge patch into existing doc
//   delete(id)            → hard delete by primary key
//   softDelete(id)        → sets _deleted:true + updatedAt
//   putMany(docs)         → bulk upsert
//   addMany(docs)         → bulk insert (skip existing)
//   clear()               → delete all docs in table
// ---------------------------------------------------------------------------
export class DexieCollection {
  constructor(private _table: TableLike) {}

  private wrapDoc(doc: Record<string, any> | null | undefined) {
    if (!doc) return null;
    return {
      ...doc,
      toJSON() {
        return { ...doc };
      },
    };
  }

  async toArray(): Promise<Record<string, any>[]> {
    try {
      const items = await this._table.toArray();
      return Array.isArray(items) ? items as Record<string, any>[] : [];
    } catch (error) {
      console.error('[DexieCollection] toArray error:', error);
      return [];
    }
  }

  async get(id: string): Promise<Record<string, any> | null> {
    try {
      if (!id) return null;
      const item = await this._table.get(id);
      return item ? (item as Record<string, any>) : null;
    } catch (error) {
      console.error('[DexieCollection] get error:', error);
      return null;
    }
  }

  /**
   * Resolve many docs by primary key in one call. Delegates to the underlying
   * table's bulkGet (native single-transaction on raw Dexie; decrypt-per-key on
   * SecureTable). Preserves the id order, mapping misses to null. Falls back to
   * parallel get() if the underlying table predates bulkGet.
   */
  async bulkGet(ids: string[]): Promise<(Record<string, any> | null)[]> {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    try {
      if (typeof (this._table as { bulkGet?: unknown }).bulkGet === 'function') {
        const items = await this._table.bulkGet(ids);
        return (Array.isArray(items) ? items : []).map((item) =>
          item ? (item as Record<string, any>) : null,
        );
      }
    } catch (error) {
      console.error('[DexieCollection] bulkGet error:', error);
    }
    return Promise.all(ids.map((id) => this.get(id)));
  }

  async put(doc: Record<string, any>): Promise<Record<string, any>> {
    if (!doc || typeof doc !== 'object') {
      throw new Error('[DexieCollection] put requires a valid document object');
    }
    const stamped = stampDoc(doc);
    await this._table.put(stamped as Record<string, unknown>);
    return stamped;
  }

  async patch(id: string, patch: Record<string, any>): Promise<void> {
    if (!id) {
      throw new Error('[DexieCollection] patch requires a valid id');
    }
    if (!patch || typeof patch !== 'object') {
      throw new Error('[DexieCollection] patch requires a valid patch object');
    }
    const existing = await this._table.get(id);
    if (!existing) return;
    const updated = { ...(existing as Record<string, any>), ...patch, updatedAt: new Date().toISOString() };
    await this._table.put(updated as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    await this._table.delete(id);
  }

  /**
   * Hard-delete many docs by primary key in one bulk operation (single transaction when the
   * underlying Dexie table supports bulkDelete). Falls back to sequential delete() otherwise.
   */
  async deleteMany(ids: string[]): Promise<void> {
    const unique = Array.from(new Set(ids.filter(Boolean)));
    if (unique.length === 0) return;
    const table = this._table as { bulkDelete?: (keys: string[]) => Promise<void> };
    if (typeof table.bulkDelete === 'function') {
      await table.bulkDelete(unique);
      return;
    }
    for (const id of unique) {
      await this._table.delete(id);
    }
  }

  async softDelete(id: string): Promise<void> {
    const existing = await this._table.get(id);
    if (existing) {
      const tombstone = { ...(existing as Record<string, any>), _deleted: true, updatedAt: new Date().toISOString() };
      await this._table.put(tombstone as Record<string, unknown>);
    }
  }

  async putMany(docs: Record<string, any>[]): Promise<{ succeeded: number; failed: number }> {
    try {
      const stamped = docs.map(stampDoc) as Record<string, unknown>[];
      await this._table.bulkPut(stamped);
      return { succeeded: docs.length, failed: 0 };
    } catch (error: any) {
      // Dexie BulkError contains failures array
      if (error.name === 'BulkError' && error.failures) {
        return { succeeded: docs.length - error.failures.length, failed: error.failures.length };
      }
      // Complete failure
      return { succeeded: 0, failed: docs.length };
    }
  }

  async addMany(docs: Record<string, any>[]): Promise<{ succeeded: number; failed: number }> {
    try {
      const stamped = docs.map(stampDoc) as Record<string, unknown>[];
      await this._table.bulkAdd(stamped);
      return { succeeded: docs.length, failed: 0 };
    } catch (error: any) {
      // Dexie BulkError contains failures array
      if (error.name === 'BulkError' && error.failures) {
        return { succeeded: docs.length - error.failures.length, failed: error.failures.length };
      }
      // Complete failure
      return { succeeded: 0, failed: docs.length };
    }
  }

  async bulkInsert(docs: Record<string, any>[]): Promise<{ succeeded: number; failed: number }> {
    return this.addMany(docs);
  }

  find() {
    return {
      exec: async () => {
        const docs = await this.toArray();
        return docs.map((doc) => this.wrapDoc(doc)).filter(Boolean);
      },
    };
  }

  /**
   * Predicate filter returning the same { toArray } shape the core-domain
   * adapter contract (DexieTableLike.filter) expects. Scans via toArray and
   * filters in memory rather than using Dexie indexes — historically because
   * tables could be encryption-wrapped (removed at v11; all tenant tables are
   * plain Dexie tables now), today because it keeps the wrapper's behavior
   * uniform and independent of index specifics.
   */
  filter(predicate: (row: Record<string, any>) => boolean): { toArray(): Promise<Record<string, any>[]> } {
    return {
      toArray: async () => (await this.toArray()).filter(predicate),
    };
  }

  findOne(id: string) {
    return {
      exec: async () => this.wrapDoc(await this.get(id)),
    };
  }

  async clear(): Promise<void> {
    await this._table.clear();
  }

  get table(): TableLike {
    return this._table;
  }
}

// ---------------------------------------------------------------------------
// Contact canonical-field backfill (schema v37 — G-004/G-018)
// ---------------------------------------------------------------------------
/**
 * @process canonicalizeContactDoc
 * Purpose: Backfill canonical contact fields from legacy flat fields.
 * Notes: Delegates to the shared `canonicalizeContactDoc` utility in
 *   `../utils/contactCanonicalization` — the single implementation used by
 *   both mobileDb and tenantDb. Re-exported here for backward compatibility
 *   with existing imports (including the migration test file).
 */
export const canonicalizeContactDoc = sharedCanonicalizeContactDoc;

// ---------------------------------------------------------------------------
// Contact mailing-address migration (schema v33)
// ---------------------------------------------------------------------------
/**
 * @process deriveMailingAddressFromLegacy
 * Purpose: Seed a structured mailingAddress from a legacy freeform address.
 * Notes: Delegates to the shared `deriveMailingAddressFromLegacy` utility in
 *   `../utils/contactCanonicalization`. Re-exported here for backward
 *   compatibility with existing imports (including the migration test file).
 */
export const deriveMailingAddressFromLegacy = sharedDeriveMailingAddressFromLegacy;

// ---------------------------------------------------------------------------
// Dexie schema — tenant collections only, no limit
// ---------------------------------------------------------------------------
function createDexieInstance(): Dexie {
  const db = new Dexie('firesquirrel-tenant');
  db.version(1).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, status, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
  });
  db.version(2).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, status, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
  });
  db.version(3).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, status, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
  });
  db.version(4).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
  });
  db.version(5).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
  });
  db.version(6).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
  });
  db.version(7).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
  });
  db.version(8).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
  });
  db.version(9).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
  });
  db.version(10).stores({
    entities:                  '_id, entityCategory, updatedAt',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
  });
  // ---------------------------------------------------------------------------
  // Version 11 — Remove encryption-at-rest (SecureTable).
  // All tenant data is public-record (parcels, buildings, hydrants, contacts,
  // inspection cases) and does not require encrypt-at-rest. Storing as plain
  // JSON makes Dexie indexes functional and eliminates the full-table-decrypt
  // bottleneck on large collections (193k+ parcels).
  //
  // Compound indexes on entities enable fast filtered + sorted queries:
  //   [entityCategory+updatedAt] — grid filtered by category, sorted by date
  //
  // The upgrade clears ALL tables because existing data was stored as encrypted
  // blobs ({ _sec: 1, _body: "..." }) that cannot be read as plaintext.
  // After upgrade, replication will re-pull all data from the server.
  // ---------------------------------------------------------------------------
  db.version(11).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], updatedAt, name',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
  }).upgrade(async (tx) => {
    console.info('[tenantDb] v11 upgrade — clearing encrypted data; sync will re-pull.');
    const tableNames = tx.storeNames;
    for (const name of tableNames) {
      await tx.table(name).clear();
    }
  });
  // ---------------------------------------------------------------------------
  // Version 12 — Add compound indexes for IDB-native cursor pagination.
  // [entityCategory+name]         — grid default sort (name A→Z per category)
  // [entityCategory+status]       — status filter without full table scan
  // [entityCategory+parcelNumber] — parcel number lookup
  // No data clear — existing plaintext data is preserved; IDB builds indexes
  // in the background on first open.
  // ---------------------------------------------------------------------------
  db.version(12).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
  });
  // No upgrade() needed — IDB builds the new compound indexes automatically
  // on the existing plaintext data. No data is lost or cleared.
  // ---------------------------------------------------------------------------
  // Version 13 — Restore email_delivery_logs table.
  // The table was included in TABLE_NAMES and TenantCollections but was absent
  // from schema versions 8-12, so users on v8+ never had the table created.
  // Re-adding it here without an upgrade() so existing tables are not touched.
  // ---------------------------------------------------------------------------
  db.version(13).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
  });
  // ---------------------------------------------------------------------------
  // Version 14 — Add email and status indexes to users table.
  // System-injected users now land in this table via /sync/pull so we need
  // these indexes for efficient local filtering (by status, by email lookup).
  // No data clear — IDB builds the new indexes on existing plaintext data.
  // ---------------------------------------------------------------------------
  db.version(14).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
  });
  // ---------------------------------------------------------------------------
  // Version 15 — Add _sync_meta table for DB identity nonce.
  // Stores a random nonce that ties localStorage sync checkpoints to THIS
  // Dexie instance. When IndexedDB is cleared, the nonce disappears and
  // replication startup clears all stale checkpoints, forcing full pulls.
  // See systemDb.ts version 9 comment for full rationale.
  // ---------------------------------------------------------------------------
  db.version(15).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    _sync_meta:                '_id',
  });
  // ---------------------------------------------------------------------------
  // Version 16 — Add report_templates table for document/permit PDF templates.
  // Separate from report_definitions (data reports/dashboards).
  // Syncs to MongoDB reportTemplates collection via replicationFactory.
  // ---------------------------------------------------------------------------
  db.version(16).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    _sync_meta:                '_id',
  });
  // ---------------------------------------------------------------------------
  // Version 17 — Add tenant-local cache tables for published system inspection
  // template library data. These tables are read-only from the tenant UI and are
  // hydrated by local-first services so browse/adopt can serve from Dexie first.
  // ---------------------------------------------------------------------------
  db.version(17).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    _sync_meta:                '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:              '_id, updatedAt',
  });
  db.version(18).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    _sync_meta:                '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:              '_id, updatedAt',
    case_number_configurations: '_id, updatedAt',
    case_number_sequences:     '_id, fiscalYear, updatedAt',
  });
  db.version(19).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    system_users_ref:          '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    _sync_meta:                '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:              '_id, updatedAt',
    case_number_configurations: '_id, updatedAt',
    case_number_sequences:     '_id, fiscalYear, updatedAt',
  });
  db.version(20).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    system_users_ref:          '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    system_import_templates:   '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    system_report_definitions: '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    system_report_templates:   '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    system_email_templates:    '_id, status, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:              '_id, updatedAt',
    case_number_configurations: '_id, updatedAt',
    case_number_sequences:     '_id, fiscalYear, updatedAt',
  });
  // ---------------------------------------------------------------------------
  // Version 21 — Add attachment_blobs table used by @firesquirrel/attachments.
  //
  // This table stores local (offline-captured) attachment Blobs until they
  // are uploaded to Azure and the confirmation window elapses. The primary
  // key is `tempId`; secondary indexes support queue scans and eviction.
  //
  // The blobs are never base64 — they are real Blob objects stored
  // natively by IndexedDB. When the upload processor confirms a blob has
  // been uploaded, the record stays around for `evictionHours` (default
  // 24 h) so UI can still show the ObjectURL preview; afterwards the
  // eviction pass deletes it.
  // ---------------------------------------------------------------------------
  db.version(21).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    system_users_ref:          '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:               '_id, updatedAt',
    import_templates:          '_id, dataMode, updatedAt',
    system_import_templates:   '_id, dataMode, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    system_report_definitions: '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    system_report_templates:   '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    system_email_templates:    '_id, status, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:              '_id, updatedAt',
    case_number_configurations: '_id, updatedAt',
    case_number_sequences:     '_id, fiscalYear, updatedAt',
    attachment_blobs:          'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
  });

  // ---------------------------------------------------------------------------
  // Version 22 — Add parentTemplateId index on import_templates and
  // system_import_templates. Enables efficient "list variants of a shell
  // template" queries without full-table scans. A template with a non-null
  // parentTemplateId is a variant (bound mapping for a specific file shape);
  // a template with null/undefined parentTemplateId is either a shell
  // (fieldMappings empty) or a standalone bound template (fieldMappings
  // populated, no parent).
  //
  // No data clear — existing templates simply have parentTemplateId
  // undefined, which correctly models them as "standalone" (shell if
  // mappings empty, bound otherwise). IDB builds the new index in the
  // background on first open.
  // ---------------------------------------------------------------------------
  db.version(22).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    system_users_ref:          '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:               '_id, updatedAt',

    import_templates:          '_id, dataMode, parentTemplateId, updatedAt',
    system_import_templates:   '_id, dataMode, parentTemplateId, updatedAt',
    import_runs:               '_id, status, startedAt',
    report_definitions:        '_id, type, status, updatedAt',
    system_report_definitions: '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    system_report_templates:   '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    system_email_templates:    '_id, status, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:              '_id, updatedAt',
    case_number_configurations: '_id, updatedAt',
    case_number_sequences:     '_id, fiscalYear, updatedAt',
    attachment_blobs:          'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',

  });
  // ---------------------------------------------------------------------------
  // Version 23 — [PHASE-1] Import centralization.
  //
  // CHANGE
  //   • Drop tenant-owned `import_templates` and `import_runs` tables.
  //     Both collections moved to the SYSTEM DB as the sole source of truth.
  //     See plans/import-system-centralization-62a7a9.md.
  //   • Add `system_import_runs` — read-only projection of the tenant's own
  //     runs, pulled via /system-sync with server-side { tenantId } filter.
  //   • `system_import_templates` already exists from earlier versions and
  //     is unchanged.
  //
  // DATA IMPACT
  //   Existing tenant Dexie data in import_templates / import_runs is
  //   discarded. This is by design — the tenant-web import UI is hidden in
  //   phase 1 and no user-facing consumer is affected. Dexie removes the
  //   omitted tables on first open at this version.
  //
  // DEFERRED / FUTURE WORK
  //   [PHASE-2] When tenant-web import UI is unhidden, consider whether
  //     `system_import_runs` should be made read-write (push allowed) for
  //     tenant-side run management. See
  //     Docs/IMPORT-CENTRALIZATION-DEFERRED.md §1.
  // ---------------------------------------------------------------------------
  db.version(23).stores({
    entities:                  '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                     '_id, entityId, caseStatus, updatedAt',
    contacts:                  '_id, updatedAt',
    templates:                 '_id, updatedAt',
    forms:                     '_id, updatedAt',
    violation_code_sets:       '_id, updatedAt',
    violation_chapters:        '_id, codeSetId, updatedAt',
    violation_index:           '_id, codeSetId, updatedAt',
    inspection_accounts:       '_id, status, updatedAt',
    inspection_account_groups: '_id, updatedAt',
    roles:                     '_id, updatedAt',
    service_area_types:        '_id, updatedAt',
    service_area_values:       '_id, serviceAreaTypeId, updatedAt',
    lookup_values:             '_id, lookupType, updatedAt',
    lookup_groups:             '_id, lookupType, updatedAt',
    lookup_categories:         '_id, updatedAt',
    users:                     '_id, email, status, updatedAt',
    system_users_ref:          '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:               '_id, updatedAt',
    // [PHASE-1] import_templates and import_runs removed. See block header.
    import_templates:          null,
    import_runs:               null,
    system_import_templates:   '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:        '_id, status, startedAt, templateId, updatedAt',
    report_definitions:        '_id, type, status, updatedAt',
    system_report_definitions: '_id, type, status, updatedAt',
    report_templates:          '_id, documentType, status, updatedAt',
    system_report_templates:   '_id, documentType, status, updatedAt',
    report_schedules:          '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                '_id, collection, documentId, action, createdAt',
    inspector_locations:       '_id, userOid, updatedAt',
    uploaded_files:            '_id, mimeType, category, updatedAt',
    generated_documents:       '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:           '_id, status, updatedAt',
    system_email_templates:    '_id, status, updatedAt',
    email_delivery_logs:       '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:      '_id, tenantId, method, updatedAt',
    widget_library:            '_id, fieldType, name, updatedAt',
    scheduled_reports:         '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:         '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:       '_id, updatedAt',
    mail_jobs:                 '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:              '_id, updatedAt',
    case_number_configurations: '_id, updatedAt',
    case_number_sequences:     '_id, fiscalYear, updatedAt',
    attachment_blobs:          'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
  });
  db.version(24).stores({
    entities:                    '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                       '_id, entityId, caseStatus, updatedAt',
    contacts:                    '_id, updatedAt',
    templates:                   '_id, updatedAt',
    forms:                       '_id, updatedAt',
    violation_code_sets:         '_id, updatedAt',
    violation_chapters:          '_id, codeSetId, updatedAt',
    violation_index:             '_id, codeSetId, updatedAt',
    inspection_accounts:         '_id, status, updatedAt',
    inspection_account_groups:   '_id, updatedAt',
    roles:                       '_id, updatedAt',
    service_area_types:          '_id, updatedAt',
    service_area_values:         '_id, serviceAreaTypeId, updatedAt',
    lookup_values:               '_id, lookupType, updatedAt',
    lookup_groups:               '_id, lookupType, updatedAt',
    lookup_categories:           '_id, updatedAt',
    users:                       '_id, email, status, updatedAt',
    system_users_ref:            '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:                 '_id, updatedAt',
    system_import_templates:     '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:          '_id, status, startedAt, templateId, updatedAt',
    report_definitions:          '_id, type, status, updatedAt',
    system_report_definitions:   '_id, type, status, updatedAt',
    report_templates:            '_id, documentType, status, updatedAt',
    system_report_templates:     '_id, documentType, status, updatedAt',
    report_schedules:            '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                  '_id, collection, documentId, action, createdAt',
    inspector_locations:         '_id, userOid, updatedAt',
    uploaded_files:              '_id, mimeType, category, updatedAt',
    generated_documents:         '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:             '_id, status, updatedAt',
    system_email_templates:      '_id, status, updatedAt',
    email_delivery_logs:         '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:        '_id, tenantId, method, updatedAt',
    widget_library:              '_id, fieldType, name, updatedAt',
    scheduled_reports:           '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:           '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:         '_id, updatedAt',
    mail_jobs:                   '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                  '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:                '_id, updatedAt',
    case_number_configurations:  '_id, updatedAt',
    case_number_sequences:       '_id, fiscalYear, updatedAt',
    attachment_blobs:            'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
    inspection_delivery_records: '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt',
    inspection_delivery_groups:  '_id, groupingKey, deliveryStatus, deliveryChannel, updatedAt',
    inspection_delivery_events:  '_id, recordId, groupId, caseId, [caseId+eventIndex], eventType, createdAt, updatedAt',
  });
  // v25: add preview_sessions — single-key (`_id: 'tenant-import-preview'`)
  // cache for in-progress preview-job state so the Import wizard can resume
  // a parse after a reload without re-uploading the source file. Local-only,
  // never synced to the server (server already owns the preview-job record).
  // See web/tenant-web/src/services/importRunService.ts → getPreviewSession.
  db.version(25).stores({
    entities:                    '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                       '_id, entityId, caseStatus, updatedAt',
    contacts:                    '_id, updatedAt',
    templates:                   '_id, updatedAt',
    forms:                       '_id, updatedAt',
    violation_code_sets:         '_id, updatedAt',
    violation_chapters:          '_id, codeSetId, updatedAt',
    violation_index:             '_id, codeSetId, updatedAt',
    inspection_accounts:         '_id, status, updatedAt',
    inspection_account_groups:   '_id, updatedAt',
    roles:                       '_id, updatedAt',
    service_area_types:          '_id, updatedAt',
    service_area_values:         '_id, serviceAreaTypeId, updatedAt',
    lookup_values:               '_id, lookupType, updatedAt',
    lookup_groups:               '_id, lookupType, updatedAt',
    lookup_categories:           '_id, updatedAt',
    users:                       '_id, email, status, updatedAt',
    system_users_ref:            '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:                 '_id, updatedAt',
    system_import_templates:     '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:          '_id, status, startedAt, templateId, updatedAt',
    report_definitions:          '_id, type, status, updatedAt',
    system_report_definitions:   '_id, type, status, updatedAt',
    report_templates:            '_id, documentType, status, updatedAt',
    system_report_templates:     '_id, documentType, status, updatedAt',
    report_schedules:            '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                  '_id, collection, documentId, action, createdAt',
    inspector_locations:         '_id, userOid, updatedAt',
    uploaded_files:              '_id, mimeType, category, updatedAt',
    generated_documents:         '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:             '_id, status, updatedAt',
    system_email_templates:      '_id, status, updatedAt',
    email_delivery_logs:         '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:        '_id, tenantId, method, updatedAt',
    widget_library:              '_id, fieldType, name, updatedAt',
    scheduled_reports:           '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:           '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:         '_id, updatedAt',
    mail_jobs:                   '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                  '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:                '_id, updatedAt',
    case_number_configurations:  '_id, updatedAt',
    case_number_sequences:       '_id, fiscalYear, updatedAt',
    attachment_blobs:            'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
    inspection_delivery_records: '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt',
    inspection_delivery_groups:  '_id, groupingKey, deliveryStatus, deliveryChannel, updatedAt',
    inspection_delivery_events:  '_id, recordId, groupId, caseId, [caseId+eventIndex], eventType, createdAt, updatedAt',
    preview_sessions:            '_id',
  });
  // ---------------------------------------------------------------------------
  // Version 26 — Add `background_jobs` table for the tether sync indicator +
  // background operations flyout (tenant scope). Server-authoritative:
  // workers emit job progress to Mongo `backgroundJobs` and clients observe
  // via Dexie live sync. NOT in TENANT_PUSH_ALLOWED_COLLECTIONS — read-only
  // from the client. Indexes optimize "active jobs by status", filter by
  // jobType, and sort by startedAt for the flyout.
  // ---------------------------------------------------------------------------
  db.version(26).stores({
    entities:                    '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                       '_id, entityId, caseStatus, updatedAt',
    contacts:                    '_id, updatedAt',
    templates:                   '_id, updatedAt',
    forms:                       '_id, updatedAt',
    violation_code_sets:         '_id, updatedAt',
    violation_chapters:          '_id, codeSetId, updatedAt',
    violation_index:             '_id, codeSetId, updatedAt',
    inspection_accounts:         '_id, status, updatedAt',
    inspection_account_groups:   '_id, updatedAt',
    roles:                       '_id, updatedAt',
    service_area_types:          '_id, updatedAt',
    service_area_values:         '_id, serviceAreaTypeId, updatedAt',
    lookup_values:               '_id, lookupType, updatedAt',
    lookup_groups:               '_id, lookupType, updatedAt',
    lookup_categories:           '_id, updatedAt',
    users:                       '_id, email, status, updatedAt',
    system_users_ref:            '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:                 '_id, updatedAt',
    system_import_templates:     '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:          '_id, status, startedAt, templateId, updatedAt',
    report_definitions:          '_id, type, status, updatedAt',
    system_report_definitions:   '_id, type, status, updatedAt',
    report_templates:            '_id, documentType, status, updatedAt',
    system_report_templates:     '_id, documentType, status, updatedAt',
    report_schedules:            '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                  '_id, collection, documentId, action, createdAt',
    inspector_locations:         '_id, userOid, updatedAt',
    uploaded_files:              '_id, mimeType, category, updatedAt',
    generated_documents:         '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:             '_id, status, updatedAt',
    system_email_templates:      '_id, status, updatedAt',
    email_delivery_logs:         '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:        '_id, tenantId, method, updatedAt',
    widget_library:              '_id, fieldType, name, updatedAt',
    scheduled_reports:           '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:           '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:         '_id, updatedAt',
    mail_jobs:                   '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                  '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:                '_id, updatedAt',
    case_number_configurations:  '_id, updatedAt',
    case_number_sequences:       '_id, fiscalYear, updatedAt',
    attachment_blobs:            'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
    inspection_delivery_records: '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt',
    inspection_delivery_groups:  '_id, groupingKey, deliveryStatus, deliveryChannel, updatedAt',
    inspection_delivery_events:  '_id, recordId, groupId, caseId, [caseId+eventIndex], eventType, createdAt, updatedAt',
    preview_sessions:            '_id',
    background_jobs:             '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
  });
  // ---------------------------------------------------------------------------
  // Version 27 — Drop `widget_library`.
  //
  // Section 2.4 removed the user-saved Widget Library feature entirely.
  // Admins now configure data-bound fields via the pre-built registry
  // (DATA_BOUND_WIDGET_REGISTRY) exposed through the "Data Widgets" palette
  // and the PropertiesPanel's registry-backed custom data-bound picker.
  //
  // The Dexie table is dropped by omission from this version's stores map.
  // Any rows that existed are discarded automatically on upgrade.
  // ---------------------------------------------------------------------------
  db.version(27).stores({
    entities:                    '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                       '_id, entityId, caseStatus, updatedAt',
    contacts:                    '_id, updatedAt',
    templates:                   '_id, updatedAt',
    forms:                       '_id, updatedAt',
    violation_code_sets:         '_id, updatedAt',
    violation_chapters:          '_id, codeSetId, updatedAt',
    violation_index:             '_id, codeSetId, updatedAt',
    inspection_accounts:         '_id, status, updatedAt',
    inspection_account_groups:   '_id, updatedAt',
    roles:                       '_id, updatedAt',
    service_area_types:          '_id, updatedAt',
    service_area_values:         '_id, serviceAreaTypeId, updatedAt',
    lookup_values:               '_id, lookupType, updatedAt',
    lookup_groups:               '_id, lookupType, updatedAt',
    lookup_categories:           '_id, updatedAt',
    users:                       '_id, email, status, updatedAt',
    system_users_ref:            '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:                 '_id, updatedAt',
    system_import_templates:     '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:          '_id, status, startedAt, templateId, updatedAt',
    report_definitions:          '_id, type, status, updatedAt',
    system_report_definitions:   '_id, type, status, updatedAt',
    report_templates:            '_id, documentType, status, updatedAt',
    system_report_templates:     '_id, documentType, status, updatedAt',
    report_schedules:            '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                  '_id, collection, documentId, action, createdAt',
    inspector_locations:         '_id, userOid, updatedAt',
    uploaded_files:              '_id, mimeType, category, updatedAt',
    generated_documents:         '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:             '_id, status, updatedAt',
    system_email_templates:      '_id, status, updatedAt',
    email_delivery_logs:         '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:        '_id, tenantId, method, updatedAt',
    // widget_library intentionally dropped (Section 2.4).
    widget_library:              null,
    scheduled_reports:           '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:           '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:         '_id, updatedAt',
    mail_jobs:                   '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                  '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:                '_id, updatedAt',
    case_number_configurations:  '_id, updatedAt',
    case_number_sequences:       '_id, fiscalYear, updatedAt',
    attachment_blobs:            'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
    inspection_delivery_records: '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt',
    inspection_delivery_groups:  '_id, groupingKey, deliveryStatus, deliveryChannel, updatedAt',
    inspection_delivery_events:  '_id, recordId, groupId, caseId, [caseId+eventIndex], eventType, createdAt, updatedAt',
    preview_sessions:            '_id',
    background_jobs:             '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
  } as any);
  // ---------------------------------------------------------------------------
  // Version 28 — Drop `users` (D-5 resolution).
  //
  // All FireSight users are stored in and owned by the SYSTEM DB, not the
  // tenant DB. Tenant-web reads and writes department members exclusively
  // via `system_users_ref` (Dexie) → /system-sync, where each pull is
  // filtered server-side by departmentMemberships.departmentId.
  //
  // The `users` table was populated by a now-dead tenant /sync pull path
  // that mapped TENANT_COLLECTION_MAP.users → MongoDB tenant `users`
  // collection. Nothing in the UI, services, or API ever read from that
  // table (all reads went to `system_users_ref`). The table is dropped by
  // omission; any rows that existed are discarded automatically on upgrade.
  //
  // See Phase 0 drift D-5 in `.windsurf/sync-readiness/`.
  // ---------------------------------------------------------------------------
  db.version(28).stores({
    entities:                    '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                       '_id, entityId, caseStatus, updatedAt',
    contacts:                    '_id, updatedAt',
    templates:                   '_id, updatedAt',
    forms:                       '_id, updatedAt',
    violation_code_sets:         '_id, updatedAt',
    violation_chapters:          '_id, codeSetId, updatedAt',
    violation_index:             '_id, codeSetId, updatedAt',
    inspection_accounts:         '_id, status, updatedAt',
    inspection_account_groups:   '_id, updatedAt',
    roles:                       '_id, updatedAt',
    service_area_types:          '_id, updatedAt',
    service_area_values:         '_id, serviceAreaTypeId, updatedAt',
    lookup_values:               '_id, lookupType, updatedAt',
    lookup_groups:               '_id, lookupType, updatedAt',
    lookup_categories:           '_id, updatedAt',
    // users intentionally dropped — system DB only, synced via system_users_ref.
    users:                       null,
    system_users_ref:            '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:                 '_id, updatedAt',
    system_import_templates:     '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:          '_id, status, startedAt, templateId, updatedAt',
    report_definitions:          '_id, type, status, updatedAt',
    system_report_definitions:   '_id, type, status, updatedAt',
    report_templates:            '_id, documentType, status, updatedAt',
    system_report_templates:     '_id, documentType, status, updatedAt',
    report_schedules:            '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                  '_id, collection, documentId, action, createdAt',
    inspector_locations:         '_id, userOid, updatedAt',
    uploaded_files:              '_id, mimeType, category, updatedAt',
    generated_documents:         '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:             '_id, status, updatedAt',
    system_email_templates:      '_id, status, updatedAt',
    email_delivery_logs:         '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:        '_id, tenantId, method, updatedAt',
    widget_library:              null,
    scheduled_reports:           '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:           '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:         '_id, updatedAt',
    mail_jobs:                   '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                  '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:                '_id, updatedAt',
    case_number_configurations:  '_id, updatedAt',
    case_number_sequences:       '_id, fiscalYear, updatedAt',
    attachment_blobs:            'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
    inspection_delivery_records: '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt',
    inspection_delivery_groups:  '_id, groupingKey, deliveryStatus, deliveryChannel, updatedAt',
    inspection_delivery_events:  '_id, recordId, groupId, caseId, [caseId+eventIndex], eventType, createdAt, updatedAt',
    preview_sessions:            '_id',
    background_jobs:             '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
  } as any);
  db.version(29).stores({
    entities:                    '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                       '_id, entityId, caseStatus, updatedAt',
    contacts:                    '_id, updatedAt',
    templates:                   '_id, updatedAt',
    forms:                       '_id, updatedAt',
    violation_code_sets:         '_id, updatedAt',
    violation_chapters:          '_id, codeSetId, updatedAt',
    violation_index:             '_id, codeSetId, updatedAt',
    inspection_accounts:         '_id, status, updatedAt',
    inspection_account_groups:   '_id, updatedAt',
    roles:                       '_id, updatedAt',
    service_area_types:          '_id, updatedAt',
    service_area_values:         '_id, serviceAreaTypeId, updatedAt',
    lookup_values:               '_id, lookupType, updatedAt',
    lookup_groups:               '_id, lookupType, updatedAt',
    lookup_categories:           '_id, updatedAt',
    users:                       null,
    system_tenant_ref:           '_id, slug, updatedAt',
    system_users_ref:            '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:                 '_id, updatedAt',
    system_import_templates:     '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:          '_id, status, startedAt, templateId, updatedAt',
    report_definitions:          '_id, type, status, updatedAt',
    system_report_definitions:   '_id, type, status, updatedAt',
    report_templates:            '_id, documentType, status, updatedAt',
    system_report_templates:     '_id, documentType, status, updatedAt',
    report_schedules:            '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                  '_id, collection, documentId, action, createdAt',
    inspector_locations:         '_id, userOid, updatedAt',
    uploaded_files:              '_id, mimeType, category, updatedAt',
    generated_documents:         '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:             '_id, status, updatedAt',
    system_email_templates:      '_id, status, updatedAt',
    email_delivery_logs:         '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:        '_id, tenantId, method, updatedAt',
    widget_library:              null,
    scheduled_reports:           '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:           '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:         '_id, updatedAt',
    mail_jobs:                   '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                  '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:                '_id, updatedAt',
    case_number_configurations:  '_id, updatedAt',
    case_number_sequences:       '_id, fiscalYear, updatedAt',
    attachment_blobs:            'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
    inspection_delivery_records: '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt',
    inspection_delivery_groups:  '_id, groupingKey, deliveryStatus, deliveryChannel, updatedAt',
    inspection_delivery_events:  '_id, recordId, groupId, caseId, [caseId+eventIndex], eventType, createdAt, updatedAt',
    preview_sessions:            '_id',
    background_jobs:             '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
  } as any);
  // ---------------------------------------------------------------------------
  // Version 30 — G-COMPAT: back-fill DocumentLayout from legacy documentConfig.
  //
  // Templates created before the Phase 0 renderer refactor stored a free-form
  // `documentConfig` bag (15-section universal builder model).  The shared
  // renderer now expects a `DocumentLayout` (band/flow model).  This upgrade
  // derives `documentLayout` from `documentConfig` for every record that has
  // the legacy field, leaving records that already have neither untouched.
  // The original `documentConfig` value is preserved — it is never deleted.
  // ---------------------------------------------------------------------------
  db.version(30).stores({
    entities:                    '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber',
    cases:                       '_id, entityId, caseStatus, updatedAt',
    contacts:                    '_id, updatedAt',
    templates:                   '_id, updatedAt',
    forms:                       '_id, updatedAt',
    violation_code_sets:         '_id, updatedAt',
    violation_chapters:          '_id, codeSetId, updatedAt',
    violation_index:             '_id, codeSetId, updatedAt',
    inspection_accounts:         '_id, status, updatedAt',
    inspection_account_groups:   '_id, updatedAt',
    roles:                       '_id, updatedAt',
    service_area_types:          '_id, updatedAt',
    service_area_values:         '_id, serviceAreaTypeId, updatedAt',
    lookup_values:               '_id, lookupType, updatedAt',
    lookup_groups:               '_id, lookupType, updatedAt',
    lookup_categories:           '_id, updatedAt',
    users:                       null,
    system_tenant_ref:           '_id, slug, updatedAt',
    system_users_ref:            '_id, authEmail, status, roleId, roleName, updatedAt',
    user_groups:                 '_id, updatedAt',
    system_import_templates:     '_id, dataMode, parentTemplateId, updatedAt',
    system_import_runs:          '_id, status, startedAt, templateId, updatedAt',
    report_definitions:          '_id, type, status, updatedAt',
    system_report_definitions:   '_id, type, status, updatedAt',
    report_templates:            '_id, documentType, status, updatedAt',
    system_report_templates:     '_id, documentType, status, updatedAt',
    report_schedules:            '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    audit_logs:                  '_id, collection, documentId, action, createdAt',
    inspector_locations:         '_id, userOid, updatedAt',
    uploaded_files:              '_id, mimeType, category, updatedAt',
    generated_documents:         '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt',
    email_templates:             '_id, status, updatedAt',
    system_email_templates:      '_id, status, updatedAt',
    email_delivery_logs:         '_id, messageId, reportDefinitionId, scheduleId, status, sentAt, updatedAt',
    email_configurations:        '_id, tenantId, method, updatedAt',
    widget_library:              null,
    scheduled_reports:           '_id, reportDefinitionId, status, nextRunAt, updatedAt',
    report_executions:           '_id, reportDefinitionId, scheduledReportId, executedAt, status',
    mail_configurations:         '_id, updatedAt',
    mail_jobs:                   '_id, status, recipientEntityId, relatedSeriesId, createdAt, updatedAt',
    _sync_meta:                  '_id',
    system_inspection_templates: '_id, status, templateType, updatedAt',
    system_forms:                '_id, updatedAt',
    case_number_configurations:  '_id, updatedAt',
    case_number_sequences:       '_id, fiscalYear, updatedAt',
    attachment_blobs:            'tempId, attachmentId, parentType, parentId, status, createdAt, uploadConfirmedAt',
    inspection_delivery_records: '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt',
    inspection_delivery_groups:  '_id, groupingKey, deliveryStatus, deliveryChannel, updatedAt',
    inspection_delivery_events:  '_id, recordId, groupId, caseId, [caseId+eventIndex], eventType, createdAt, updatedAt',
    preview_sessions:            '_id',
    background_jobs:             '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
  } as any).upgrade(async (tx) => {
    const TEMPLATE_TABLES = ['report_templates', 'system_report_templates'] as const;
    for (const tableName of TEMPLATE_TABLES) {
      await tx.table(tableName).toCollection().modify((record: Record<string, any>) => {
        if (
          record.documentConfig !== undefined &&
          record.documentConfig !== null &&
          typeof record.documentConfig === 'object' &&
          record.documentLayout === undefined
        ) {
          record.documentLayout = documentConfigToLayout(record.documentConfig);
        }
      });
    }
  });

  // ---------------------------------------------------------------------------
  // Version 31 — Add fee_schedules table.
  // Server-authoritative: the AI parse worker extracts FeeRuleLocal[] from
  // uploaded ordinance / fee-schedule documents and writes them via system-api.
  // Clients pull-only via sync; NOT in TENANT_PUSH_ALLOWED_COLLECTIONS.
  // ---------------------------------------------------------------------------
  db.version(31).stores({
    fee_schedules: '_id, status, documentType, effectiveDate, sourceDocumentId, updatedAt',
  });

  // ---------------------------------------------------------------------------
  // Version 32 — Lifecycle correction (Decision D13-R).
  // Removes the legacy multi-user approval workflow in favour of a single-author
  // lifecycle:
  //   • generated_documents: drop approvalStatus/approvedBy/approvedAt/
  //     approvalComment/rejectedBy/rejectedAt/rejectionReason/changesRequestedBy/
  //     changesRequestedAt/resubmittedBy/resubmittedAt. Backfill `deliveryStatus`
  //     to 'generated' when absent. distributionStatus, distributionRecipients
  //     and per-recipient emailStatus are preserved untouched.
  //   • report_templates / system_report_templates: backfill
  //     documentLayout.publishedStatus to 'draft' when absent so existing
  //     templates remain editable but are not auto-published.
  // Pure data migration — no index changes, so stores() is left empty.
  // ---------------------------------------------------------------------------
  db.version(32).stores({}).upgrade(async (tx) => {
    const APPROVAL_FIELDS = [
      'approvalStatus', 'approvedBy', 'approvedAt', 'approvalComment',
      'rejectedBy', 'rejectedAt', 'rejectionReason',
      'changesRequestedBy', 'changesRequestedAt',
      'resubmittedBy', 'resubmittedAt',
    ] as const;

    await tx.table('generated_documents').toCollection().modify((record: Record<string, any>) => {
      for (const field of APPROVAL_FIELDS) {
        if (field in record) delete record[field];
      }
      if (record.deliveryStatus === undefined) {
        record.deliveryStatus = 'generated';
      }
    });

    const TEMPLATE_TABLES = ['report_templates', 'system_report_templates'] as const;
    for (const tableName of TEMPLATE_TABLES) {
      await tx.table(tableName).toCollection().modify((record: Record<string, any>) => {
        if (
          record.documentLayout !== undefined &&
          record.documentLayout !== null &&
          typeof record.documentLayout === 'object' &&
          record.documentLayout.publishedStatus === undefined
        ) {
          record.documentLayout.publishedStatus = 'draft';
        }
      });
    }
  });

  // ---------------------------------------------------------------------------
  // Version 33 — Structured contact mailing address (IRM #144 Phase 2).
  // Seeds `contacts.mailingAddress` (PostalAddress shape) from the legacy
  // freeform `address` string so existing contacts surface a validatable
  // address with status 'needsValidation'. No index change (mailingAddress is
  // a non-indexed field), so stores() is left empty.
  // ---------------------------------------------------------------------------
  db.version(33).stores({}).upgrade(async (tx) => {
    await tx.table('contacts').toCollection().modify(deriveMailingAddressFromLegacy);
  });

  // ---------------------------------------------------------------------------
  // Version 34 — Inspection print-batch tracking (IRM #143 Phase 2).
  // Additive: introduces the `inspection_print_batches` table that records a
  // user-created bulk-print batch and its print → (partial) send lifecycle.
  // No data transform is needed (new empty table), so the upgrade callback is
  // omitted; existing rows in all carried-forward tables are untouched.
  // ---------------------------------------------------------------------------
  db.version(34).stores({
    inspection_print_batches: '_id, status, createdAt, updatedAt',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 35 — Report version control + legal evidence (IRM #144 Phase 5).
  // Additive: introduces the `generated_document_versions` table that records an
  // immutable per-send reference (blob + content checksum + recipient snapshot)
  // for each inspection-report send, so the exact report sent is reproducible for
  // court review. New optional version fields (reportVersionId, reportChecksum,
  // dataChangedAt) on `inspection_delivery_records` are non-indexed and require no
  // store change. No data transform is needed (new empty table), so the upgrade
  // callback is omitted; existing rows in all carried-forward tables are untouched.
  // ---------------------------------------------------------------------------
  db.version(35).stores({
    generated_document_versions:
      '_id, recordId, caseId, generatedDocumentId, reportChecksum, [recordId+versionNumber], createdAt, updatedAt',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 36 — SMS (ACS) + stamps.com delivery seams (IRM #144 Phase 6).
  // Additive, flag-gated (default off): introduces two append-only ledgers —
  //   • inspection_sms_deliveries: per-send SMS occurrences (queued → sent/failed)
  //     for the Azure Communication Services text channel.
  //   • inspection_postage_usage: stamps.com postage/label usage entries for
  //     provider_mail deliveries, keyed for per-department cost allocation.
  // New optional fields on existing rows (recipient.phoneNumbers,
  // record.deliverySmsSnapshot / mailProvider) are non-indexed and require no
  // store change. No data transform is needed (new empty tables), so the upgrade
  // callback is omitted; existing rows in all carried-forward tables are untouched.
  // ---------------------------------------------------------------------------
  db.version(36).stores({
    inspection_sms_deliveries:
      '_id, recordId, caseId, reportVersionId, status, queuedAt, createdAt, updatedAt',
    inspection_postage_usage:
      '_id, recordId, caseId, reportVersionId, inspectionAccountId, status, recordedAt, createdAt, updatedAt',
  } as any);
  // ---------------------------------------------------------------------------
  // Version 37 — Add isArchived index to cases table.
  // Enables efficient Dexie queries for archived/non-archived case filtering
  // (G-505). Purely additive — no data transform required.
  // ---------------------------------------------------------------------------
  db.version(37).stores({
    cases: '_id, entityId, caseStatus, isArchived, updatedAt',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 37 — Contacts index optimization + canonical-field backfill
  // (G-004/G-015/G-016/G-018).
  // Index change: adds `status` and `name` indexes to the contacts table so
  //   that common queries — "active contacts sorted alphabetically" — use an
  //   indexed scan instead of a full table scan + in-memory sort.
  // Data migration: backfills canonical fields (displayName, firstName,
  //   lastName, phoneNumbers[], emails[], defaultRoles[]) from legacy flat
  //   fields (phone, email, role, contactType) for any contacts that only have
  //   legacy data. Mirrors mobile-pwa's normalizeMobileContactDoc (v12).
  // ---------------------------------------------------------------------------
  db.version(37).stores({
    contacts: '_id, status, name, updatedAt',
  }).upgrade(async (tx) => {
    await tx.table('contacts').toCollection().modify(canonicalizeContactDoc);
  });

  // ---------------------------------------------------------------------------
  // Version 38 — API usage cache (Phase 5.10).
  // NOT a synced collection — this table is NOT in TENANT_COLLECTION_MAP and
  // does not go through the standard sync pull/push pipeline. Usage meters
  // live in the SYSTEM database (platform-level, cross-tenant), which
  // tenant-web's Dexie has no sync path to. Instead, apiUsageService.ts
  // fetches GET /api/usage directly (an inherently-online, tenant-scoped
  // read — the department's own current-month usage) and writes the result
  // here as a local cache for the UI to read via
  // liveQuery. Single row, keyed by the literal string 'own'.
  // ---------------------------------------------------------------------------
  db.version(38).stores({
    api_usage_cache: '_id, fetchedAt',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 39 — Geo-Import (Phase 6-8).
  // Three new local-only tables backing the geo-import frontend chain:
  //   - geo_import_runs:   local cache of import run metadata + dry-run results
  //     fetched from /api/geo-import/status/:jobId. NOT in the standard sync
  //     pipeline — geoImportService writes here after each API call so the UI
  //     can read progress via liveQuery without re-polling the server.
  //   - geo_import_files:  local cache of available GeoParquet files listed
  //     from /api/geo-import/available-files. Refreshed on panel mount.
  //   - update_token_usage: local cache of update-token entitlement + usage
  //     data (Phase 8). updateTokenService writes here after each check-update
  //     / run-update API call so the Parcel Data Settings UI can display token
  //     counts via liveQuery without re-polling the server.
  // None of these are in TENANT_COLLECTION_MAP — they are local caches only.
  // ---------------------------------------------------------------------------
  db.version(39).stores({
    api_usage_cache: '_id, fetchedAt',
    geo_import_runs: '_id, status, filterMode, createdAt, updatedAt',
    geo_import_files: '_id, container, filename, lastModified',
    update_token_usage: '_id, fetchedAt',
  } as any);

  // v40: Add `_db_meta` table for local database identity (Local Database
  // Manager — Phase 1). This single-record table stores the database's
  // identity (dbId, environment, schemaVersion, appVersion, apiBaseUrl) so
  // the localDatabaseManager can detect cross-environment data bleed and
  // trigger recovery. The table is NOT in any sync config map — it is
  // written only by localDatabaseManager during creation or after recovery.
  // No data transformation needed — just adding the table.
  db.version(40).stores({
    _db_meta: '_id',
  } as any);

  // v41: Repair the v23 import-centralization drop for databases that already
  // upgraded while the historical migration only omitted the old stores.
  // Dexie carries omitted stores forward, so explicit null declarations are
  // required to remove these obsolete tenant-owned local caches.
  db.version(41).stores({
    import_templates: null,
    import_runs: null,
  } as any);

  // v42: Add field_definitions, occupancy_chemicals, and occupancy_tanks tables
  // from the dev branch (HEAD) that were not included in stage v38-v41.
  db.version(42).stores({
    field_definitions: '_id, targetCollection, fieldKey, updatedAt',
    occupancy_chemicals: '_id, entityId, chemicalName, updatedAt',
    occupancy_tanks: '_id, entityId, tankNumber, updatedAt',
  } as any);

  // v43: Drop the retired geo-import local caches. The standalone
  // /api/geo-import surface was removed (manual-importer Phase 2) — persisted
  // GeoParquet selection now lives in the unified import wizard and runs
  // through ordinary ImportRuns, so these local-only caches have no readers
  // or writers left. Explicit null declarations are required — Dexie carries
  // omitted stores forward. update_token_usage stays (live token flow).
  db.version(43).stores({
    geo_import_runs: null,
    geo_import_files: null,
  } as any);

  // v44: Complex Inspections Phase 1B — pull-only `complex_entity_groups`
  // sync table, entity complex-facet indexes, and the local-only
  // `complex_mutations` operation outbox. Additive only — all v43 rows and
  // indexes carry forward.
  db.version(44).stores({
    complex_entity_groups: '_id, groupKind, anchor.entityId, status, updatedAt',
    entities:
      '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber, complexAnchor.groupId, complexMembership.groupId, complexMembership.membershipStatus',
    complex_mutations: '_id, operationId, status, operationType, createdAt, updatedAt',
  } as any);

  // v45: Complex Inspections Phase 1C — multi-entry case indexes on the
  // event-derived visit projections (complexVisitIds / complexGroupIds /
  // complexRootVisitIds) for local-first visit listing and progress queries.
  // Additive only — all v44 rows and indexes carry forward.
  db.version(45).stores({
    cases:
      '_id, entityId, caseStatus, isArchived, updatedAt, *complexVisitIds, *complexGroupIds, *complexRootVisitIds',
  } as any);

  // v46: Complex Inspections Phase 2A — additive compound indexes for active
  // member/group queries plus grouped report and delivery review. Index-only:
  // no documents are rewritten and all v45 data is preserved byte-for-byte.
  db.version(46).stores({
    complex_entity_groups:
      '_id, groupKind, anchor.entityId, status, updatedAt, [groupKind+status], [status+updatedAt]',
    entities:
      '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber, complexAnchor.groupId, complexMembership.groupId, complexMembership.membershipStatus, [complexMembership.groupId+complexMembership.membershipStatus]',
    generated_documents:
      '_id, inspectionSeriesId, documentType, reportTemplateId, updatedAt, generatedAt, reportDataScope, visitId, groupId, generationIdentity, [groupId+generatedAt], [reportDataScope+generatedAt]',
    inspection_delivery_records:
      '_id, caseId, [caseId+eventIndex], generatedDocumentId, deliveryStatus, deliveryChannel, deliveryGroupId, groupingKey, inspectionAccountId, updatedAt, billingGroupKey, reportSource.groupId, [reportSource.groupId+updatedAt]',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 47 — Inspection Programs & Entity Scheduling (#737 Phase 1),
  // re-based from dev's v44 (both branches bumped v44-v46 for different
  // tables; dev's chain is appended here so browsers that already installed
  // either branch's v46 still receive the other branch's tables).
  // Five new tenant-owned tables backing the recurring inspection-program
  // model. Purely additive — no data transform required.
  //   - inspection_programs:        program definitions (12 system-replayed
  //                                 predefined + tenant custom). `hidden` is
  //                                 tenant-controlled; `memberCount` denormalized.
  //   - auto_schedule_rules:        tenant-owned scheduler rule config
  //                                 (materialization / year-end toggles).
  //   - carry_forward_rules:        tenant-owned violation carry-forward config.
  //   - schedule_execution_runs:    server-authoritative run-level history —
  //                                 written by workers, read-only on client.
  //   - schedule_execution_evidence: server-authoritative per-entity action
  //                                 detail; indexed on entityId (entity
  //                                 Execution History) and runId (step-18
  //                                 run drill-down).
  // ---------------------------------------------------------------------------
  db.version(47).stores({
    inspection_programs: '_id, name, isPredefined, active, hidden, updatedAt',
    auto_schedule_rules: '_id, enabled, updatedAt',
    carry_forward_rules: '_id, enabled, updatedAt',
    schedule_execution_runs: '_id, runType, startedAt',
    schedule_execution_evidence: '_id, entityId, runId, recordedAt',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 48 — #737 Phase 5a duplicate-detection indexes on `cases`
  // (re-based from dev's v45). detectInspectionDuplicates (§10.3) keys on
  // entityId + fiscalYear, then filters caseType.code / inspectionType
  // (event-level) / caseStatus in memory — the compound index narrows the
  // candidate set; the dotted single covers case-type lookups. Purely
  // additive — no data transform.
  // ---------------------------------------------------------------------------
  db.version(48).stores({
    cases: '_id, entityId, caseStatus, fiscalYear, updatedAt, caseType.code, [entityId+fiscalYear]',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 49 — #737 Phase 6 schedule-rule catalog (re-based from dev's
  // v46). `schedule_rule_catalog` is the system-managed rule-type catalog
  // replayed read-only into the tenant DB (plan §11.3). The tenant-web
  // Auto-Schedule Rules page reads it for the rule-type/condition/action
  // option menus. Additive — no data transform.
  // ---------------------------------------------------------------------------
  db.version(49).stores({
    schedule_rule_catalog: '_id, ruleTypeCode, inheritedFromId, updatedAt',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 50 — Restore the `cases` indexes v48 dropped. Dexie replaces a
  // table's whole index list whenever a later version redeclares it, so v48's
  // duplicate-detection `cases` string (re-based from dev) silently removed
  // v37's isArchived and v45's complex visit multi-entry indexes, breaking
  // `where('complexGroupIds')` in complexVisitService. This declares the union
  // of both chains. Index-only — no documents are rewritten. v48 stays as
  // shipped because browsers that already installed it must still upgrade.
  // ---------------------------------------------------------------------------
  db.version(50).stores({
    cases:
      '_id, entityId, caseStatus, isArchived, fiscalYear, updatedAt, caseType.code, [entityId+fiscalYear], *complexVisitIds, *complexGroupIds, *complexRootVisitIds',
  } as any);

  // Customer Portal Phase 2 — workflow engine collections.
  //   portal_requests:         customer workflow submissions + thread —
  //     pull-only (written via /api/portal/field-requests/* boundaries).
  //   portal_change_requests:  customer contact-edit approval queue — pull-only
  //     (written via /api/portal/pending-changes/* boundaries).
  db.version(51).stores({
    portal_requests: '_id, status, workflowId, workflowCategory, entityId, customerAccountId, updatedAt',
    portal_change_requests: '_id, status, entityId, submittedAt',
  } as any);

  // Customer Portal Phase 2b, schema v52.
  //   portal_activity_log: customer interaction audit trail written only by
  //     customer-portal-api — pull-only read surface for the Customer Portal
  //     admin pages (Overview feed, Activity Log, embedded contact panel).
  db.version(52).stores({
    portal_activity_log: '_id, customerAccountId, contactId, eventType, timestamp',
  } as any);

  // Customer Portal Phase 3, schema v53.
  //   self_certification_submissions: customer repair-evidence/self-cert
  //     attestations reviewed in the portal-admin queue — pull-only (written
  //     via /api/portal/self-cert-reviews/:id/review boundary).
  db.version(53).stores({
    self_certification_submissions: '_id, status, targetType, caseId, entityId, customerAccountId, createdAt',
  } as any);

  // Customer Portal Phase 6, schema v54.
  //   public_safety_surveys: household safety profiles from the anonymous
  //     public-education survey — pull-only; feeds the dispatch-map
  //     meeting-place layer + public education admin page.
  db.version(54).stores({
    public_safety_surveys: '_id, entityId, normalizedAddress, submissionId, createdAt',
  } as any);

  // v55 — report parameters: multi-entry indexes on derived primitive arrays.
  // parameterIds/boundParameterIds are absent on legacy docs (multi-entry indexes
  // ignore absent fields) — additive, no upgrade needed.
  db.version(55).stores({
    report_definitions: '_id, type, status, updatedAt, *parameterIds',
    system_report_definitions: '_id, type, status, updatedAt, *parameterIds',
    report_schedules: '_id, reportDefinitionId, isActive, nextRunAt, updatedAt, *boundParameterIds',
  } as any);

  // v56 — entities.version index: lets the entity-grid change signature detect
  // version/_deleted bumps that carry no new updatedAt (index-only read, no
  // document loads). Additive — Dexie builds the index in the background.
  db.version(56).stores({
    entities:
      '_id, entityCategory, [entityCategory+updatedAt], [entityCategory+name], [entityCategory+status], updatedAt, name, parcelNumber, complexAnchor.groupId, complexMembership.groupId, complexMembership.membershipStatus, [complexMembership.groupId+complexMembership.membershipStatus], version',
  } as any);

  // ---------------------------------------------------------------------------
  // Version 57 — Reconcile the v32 deliveryStatus backfill with the resolved
  // distribution state. v32 stamped deliveryStatus:'generated' on every
  // generated_documents record missing the field. The report projector emits
  // distributionStatus only when deliveryStatus is absent, so a backfilled
  // 'generated' hides the real distribution outcome in previews while
  // scheduled runs show it. The generation worker advances deliveryStatus on
  // send results, so 'generated' alongside a RESOLVED distributionStatus is
  // always a backfill; 'pending'/'skipped' distributions stay 'generated'
  // (worker-authored new docs share that combination — cannot distinguish).
  // distributionStatus:'distributed' maps to the delivery enum's 'delivered'.
  // ---------------------------------------------------------------------------
  db.version(57).stores({}).upgrade(async (tx) => {
    await tx.table('generated_documents').toCollection().modify((record: Record<string, any>) => {
      const dist = record.distributionStatus;
      if (
        record.deliveryStatus === 'generated' &&
        typeof dist === 'string' &&
        !['generated', 'pending', 'skipped'].includes(dist)
      ) {
        record.deliveryStatus = dist === 'distributed' ? 'delivered' : dist;
      }
    });
  });

  // Customer Portal — customerQuestions pull-only mirror, schema v58.
  //   Customer↔inspector question threads are written by customer-portal-api
  //   (POST /api/questions) and answered through the dedicated
  //   /api/portal/questions/:id/reply boundary — the Field Requests page reads
  //   the synced rows so inspectors see customer questions without an API call.
  db.version(58).stores({
    customer_questions: '_id, status, caseId, entityId, customerAccountId, updatedAt',
  } as any);

  return db;
}
// TenantDatabase — shape returned by getTenantDb()
// Exposes both db.entities AND db.collections.entities for compatibility.
// ---------------------------------------------------------------------------
export type TenantCollections = {
  entities: DexieCollection;
  cases: DexieCollection;
  contacts: DexieCollection;
  templates: DexieCollection;
  forms: DexieCollection;
  system_inspection_templates: DexieCollection;
  system_forms: DexieCollection;
  violation_code_sets: DexieCollection;
  violation_chapters: DexieCollection;
  violation_index: DexieCollection;
  inspection_accounts: DexieCollection;
  inspection_account_groups: DexieCollection;
  roles: DexieCollection;
  service_area_types: DexieCollection;
  service_area_values: DexieCollection;
  lookup_values: DexieCollection;
  lookup_groups: DexieCollection;
  lookup_categories: DexieCollection;
  field_definitions: DexieCollection;
  occupancy_chemicals: DexieCollection;
  occupancy_tanks: DexieCollection;
  // [D-5] users dropped at schema v28 — synced via system_users_ref / /system-sync.
  system_tenant_ref: DexieCollection;
  system_users_ref: DexieCollection;
  user_groups: DexieCollection;
  // [PHASE-1] import_templates and import_runs removed at schema v23.
  // See plans/import-system-centralization-62a7a9.md.
  system_import_templates: DexieCollection;

  system_import_runs: DexieCollection;
  report_definitions: DexieCollection;
  system_report_definitions: DexieCollection;
  report_templates: DexieCollection;
  system_report_templates: DexieCollection;
  report_schedules: DexieCollection;
  audit_logs: DexieCollection;
  inspector_locations: DexieCollection;
  uploaded_files: DexieCollection;
  generated_documents: DexieCollection;
  email_templates: DexieCollection;
  system_email_templates: DexieCollection;
  email_delivery_logs: DexieCollection;
  email_configurations: DexieCollection;
  // Section 2.4: widget_library removed from TenantCollections shape.
  scheduled_reports: DexieCollection;
  report_executions: DexieCollection;
  mail_configurations: DexieCollection;
  mail_jobs: DexieCollection;
  inspection_delivery_records: DexieCollection;
  inspection_delivery_groups: DexieCollection;
  inspection_delivery_events: DexieCollection;
  inspection_print_batches: DexieCollection;
  generated_document_versions: DexieCollection;
  inspection_sms_deliveries: DexieCollection;
  inspection_postage_usage: DexieCollection;
  case_number_configurations: DexieCollection;
  case_number_sequences: DexieCollection;
  preview_sessions: DexieCollection;
  background_jobs: DexieCollection;
  fee_schedules: DexieCollection;
  // Phase 5.10 — local cache only, NOT synced via the standard pipeline.
  api_usage_cache: DexieCollection;
  // Phase 8 — update-token entitlement cache, NOT synced via the standard
  // pipeline. (The Phase 6 geo_import_runs / geo_import_files caches were
  // dropped at schema v43 with the retired /api/geo-import surface.)
  update_token_usage: DexieCollection;
  // Complex Inspections Phase 1B (v44): pull-only sync table for
  // `inspectableEntityGroups` — server-writes via /api/complex-mutations only.
  complex_entity_groups: DexieCollection;
  // Complex Inspections Phase 1B (v44): local-only mutation outbox; never
  // synced via the standard pipeline (TENANT_LOCAL_ONLY_TABLES).
  complex_mutations: DexieCollection;
  // Inspection Programs & Entity Scheduling (#737 Phase 1, schema v47).
  inspection_programs: DexieCollection;
  auto_schedule_rules: DexieCollection;
  carry_forward_rules: DexieCollection;
  schedule_execution_runs: DexieCollection;
  schedule_execution_evidence: DexieCollection;
  // #737 Phase 6, schema v49 — system rule-type catalog, pull-only.
  schedule_rule_catalog: DexieCollection;
  // Customer Portal Phase 2, schema v51 — workflow requests + pending contact
  // changes, both pull-only (written via dedicated /api/portal/* boundaries).
  portal_requests: DexieCollection;
  portal_change_requests: DexieCollection;
  portal_activity_log: DexieCollection;
  self_certification_submissions: DexieCollection;
  // Customer Portal Phase 6, schema v54 — public-education household profiles.
  public_safety_surveys: DexieCollection;
  // schema v58 — customer↔inspector question threads (pull-only mirror).
  customer_questions: DexieCollection;
};

export interface TenantDatabase extends TenantCollections {
  collections: TenantCollections;
  close(): Promise<void>;
}

const TABLE_NAMES = [
  'entities', 'cases', 'contacts', 'templates', 'forms',
  'system_inspection_templates', 'system_forms',
  'violation_code_sets', 'violation_chapters', 'violation_index',
  'inspection_accounts', 'inspection_account_groups', 'roles',
  'service_area_types', 'service_area_values',
  'lookup_values', 'lookup_groups', 'lookup_categories',

  // [PHASE-1] 'import_templates' and 'import_runs' dropped at v23.
  // [D-5] 'users' dropped at v28 — system DB only, synced via system_users_ref.
  'system_tenant_ref', 'system_users_ref', 'user_groups', 'system_import_templates', 'system_import_runs',
  'report_definitions', 'system_report_definitions', 'report_templates', 'system_report_templates', 'report_schedules',
  'audit_logs', 'inspector_locations',
  'uploaded_files', 'generated_documents', 'email_templates', 'system_email_templates',
  'email_delivery_logs', 'email_configurations',
  // Section 2.4: 'widget_library' removed from TABLE_NAMES.
  'scheduled_reports', 'report_executions',
  'mail_configurations', 'mail_jobs',
  'inspection_delivery_records', 'inspection_delivery_groups', 'inspection_delivery_events',
  'inspection_print_batches',
  'generated_document_versions',
  'inspection_sms_deliveries', 'inspection_postage_usage',
  'case_number_configurations', 'case_number_sequences',
  'preview_sessions',
  'background_jobs',
  'fee_schedules',
  // v42 tables — schema stores exist, but getTenantDb() only wraps names
  // listed here. Omitting them left db.field_definitions undefined and
  // occupancy chemicals/tanks unreachable after sync.
  'field_definitions',
  'occupancy_chemicals',
  'occupancy_tanks',
  'api_usage_cache',
  // 'geo_import_runs' and 'geo_import_files' dropped at v43.
  'update_token_usage',
  // Complex Inspections Phase 1B (v44): pull-only group table + local-only
  // mutation outbox.
  'complex_entity_groups',
  'complex_mutations',
  // Inspection Programs & Entity Scheduling (#737 Phase 1, schema v47).
  'inspection_programs',
  'auto_schedule_rules',
  'carry_forward_rules',
  'schedule_execution_runs',
  'schedule_execution_evidence',
  // #737 Phase 6, schema v49 — system rule-type catalog (pull-only mirror).
  'schedule_rule_catalog',
  // Customer Portal Phase 2, schema v51 — portal workflow queue tables.
  'portal_requests',
  'portal_change_requests',
  // Customer Portal Phase 2b, schema v52 — customer activity audit trail.
  'portal_activity_log',
  // Customer Portal Phase 3, schema v53 — self-cert review queue.
  'self_certification_submissions',
  // Customer Portal Phase 6, schema v54 — public-education survey profiles.
  'public_safety_surveys',
  // schema v58 — customer↔inspector question threads (pull-only mirror).
  'customer_questions',
] as const;

function buildTenantDatabase(dexie: Dexie): TenantDatabase {
  const cols = {} as Record<string, DexieCollection>;
  for (const name of TABLE_NAMES) {
    // Plain Dexie.Table — no encryption for public-record tenant data
    cols[name] = new DexieCollection(dexie.table(name) as any);
  }
  return {
    ...(cols as TenantCollections),
    collections: cols as TenantCollections,
    close: () => { dexie.close(); return Promise.resolve(); },
  };
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------
let dbPromise: Promise<TenantDatabase> | null = null;
let resolvedDb: TenantDatabase | null = null;
let rawDexie: Dexie | null = null;

export function getTenantDb(): Promise<TenantDatabase> {
  if (!dbPromise) dbPromise = openTenantDb();
  return dbPromise;
}

/**
 * Synchronous accessor for the already-open TenantDatabase, or null before the
 * first successful getTenantDb() resolves.
 *
 * Services called inside a liveQuery observed function must resolve the db
 * WITHOUT `await` — awaiting the cached native promise escapes Dexie's
 * observation zone, so the table read never registers and the subscription
 * never re-emits. The canonical pattern in service reads is:
 *
 *   const db = peekTenantDb() ?? await getTenantDb();
 *
 * which is synchronous post-boot and falls back to the awaited open path only
 * before the first open completes.
 */
export function peekTenantDb(): TenantDatabase | null {
  return resolvedDb;
}

/**
 * Returns the underlying raw Dexie instance so callers can subscribe to
 * db.on('changes') for reactive UI updates without going through the
 * DexieCollection wrapper chain.
 * Returns null if the DB has not been opened yet.
 */
export function getDexieRaw(): Dexie | null {
  return rawDexie;
}

/**
 * Returns true if getTenantDb() has already been called (DB open is in-flight
 * or complete). Used by LocalDbContext to skip the loading spinner when the DB
 * was pre-opened in main.tsx before React mounted.
 */
export function isTenantDbOpen(): boolean {
  return dbPromise !== null;
}

// No native timeout on a blocked IDB upgrade open — the 'blocked' listener
// above only logs; it does not unblock. Without a bounded timeout, a stale
// tab/connection holding an older schema version strands the app in
// 'initializing' forever with no error surfaced anywhere.
const TENANT_OPEN_TIMEOUT_MS = 10_000;

async function openTenantDb(): Promise<TenantDatabase> {
  const dexie = createDexieInstance();
  dexie.on('blocked', () => {
    console.warn(
      '[tenantDb] IDB upgrade blocked — another tab is holding an older version of firesquirrel-tenant. ' +
      'Close all other tabs using this app and reload.',
    );
  });
  // If the timeout wins the race, dexie.open() may still be pending and could
  // resolve later, leaving an orphaned IDB connection that blocks future opens.
  // Track the timeout state so we can close any connection that establishes late.
  let timedOut = false;
  const openPromise = dexie.open().then(() => {
    if (timedOut) {
      try { dexie.close(); } catch { /* instance already closed */ }
    }
  });
  try {
    await Promise.race([
      openPromise,
      new Promise<never>((_, reject) => setTimeout(
        () => reject(new Error(
          `Timed out opening firesquirrel-tenant after ${TENANT_OPEN_TIMEOUT_MS}ms — likely blocked by another ` +
          'open tab/connection holding an older schema version. Close other tabs of this app and reload.',
        )),
        TENANT_OPEN_TIMEOUT_MS,
      )),
    ]);
  } catch (err) {
    timedOut = true;
    // Abort any in-flight open and release any connection that does get established.
    try { dexie.close(); } catch { /* instance may not be open yet */ }
    throw err;
  }
  rawDexie = dexie;
  resolvedDb = buildTenantDatabase(dexie);
  return resolvedDb;
}

export async function destroyTenantDb(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    await db.close();
    dbPromise = null;
    resolvedDb = null;
    rawDexie = null;
  }
}
